import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, RefreshCw } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { getErrorDetail } from '../../../utils/errorUtils';
import { AssignableCampaign, MailboxAssignUpdates, MailboxRow } from '../../../types/mailbox';
import { useToastContext } from '../../../contexts/ToastContext';
import { Badge, Button, ConfirmationModal, Spinner } from '../../ui';
import { CustomSelect } from '../../common/CustomSelect';

/** Admin mapping of this account's sending inboxes to campaigns, in any workspace that sends on
 * the same account. Provider facts and the warm-up score come from the hourly sync. */

const UNASSIGNED = '';

const stateBadge = (row: MailboxRow): { label: string; variant: 'success' | 'error' } => {
  if (row.warmup_status === 'burned') return { label: 'burned', variant: 'error' };
  return row.not_ready_reason
    ? { label: 'unusable', variant: 'error' }
    : { label: 'usable', variant: 'success' };
};

const warmupFact = (row: MailboxRow): string =>
  row.not_ready_reason ?? (row.warmup_score === null ? 'Not scored yet' : `Score ${row.warmup_score}`);

type Option = { value: string; label: string };

const byLabel = (a: Option, b: Option) => a.label.toLowerCase().localeCompare(b.label.toLowerCase());

/** The options with the row's current choice added by name, in order, when the list lacks it. */
const withCurrent = (options: Option[], value: string | null, name: string | null): Option[] => {
  if (!value || options.some((o) => o.value === value)) return options;
  const [unassigned, ...rest] = options;
  return [unassigned, ...[...rest, { value, label: name ?? 'Unknown' }].sort(byLabel)];
};

const campaignLabel = (campaign: AssignableCampaign): string =>
  campaign.workspace_name ? `${campaign.name} — ${campaign.workspace_name}` : campaign.name;

const UNASSIGNED_OPTION: Option = { value: UNASSIGNED, label: 'Unassigned' };

const PROVIDER_BADGE: Record<
  MailboxRow['provider_status'],
  'success' | 'warning' | 'error' | 'neutral'
> = {
  active: 'success',
  paused: 'warning',
  error: 'error',
  missing: 'neutral',
};


export const MailboxPoolTab: React.FC = () => {
  const { showToast } = useToastContext();

  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  const [campaigns, setCampaigns] = useState<AssignableCampaign[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [savingEmails, setSavingEmails] = useState<ReadonlySet<string>>(new Set());
  const [unburning, setUnburning] = useState<MailboxRow | null>(null);

  // First paint waits only on the pool; the campaign list hydrates on its own.
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setMailboxes(await apiClient.getMailboxPool());
    } catch (error) {
      console.error('Failed to load mailbox pool:', error);
      showToast(getErrorDetail(error, 'Failed to load mailbox pool'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    apiClient.getAssignableCampaigns().then(
      (rows) => { if (!cancelled) setCampaigns(rows); },
      (error) => {
        console.error('Failed to load campaigns:', error);
        if (cancelled) return;
        setCampaigns([]);
        showToast('Campaigns failed to load — refresh to retry', 'error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const campaignsLoading = campaigns === null;

  const campaignOptions = useMemo(
    () => [
      UNASSIGNED_OPTION,
      ...(campaigns ?? [])
        .map((c) => ({ value: c.id, label: campaignLabel(c) }))
        .sort(byLabel),
    ],
    [campaigns]
  );

  /** An inbox that cannot be used is not offered for assignment, only for release. */
  const optionsFor = (row: MailboxRow): Option[] =>
    row.not_ready_reason
      ? withCurrent([UNASSIGNED_OPTION], row.gtm_project_id, row.project_name)
      : withCurrent(campaignOptions, row.gtm_project_id, row.project_name);

  const needReconnecting = useMemo(
    () => mailboxes.filter((m) => m.provider_status === 'error').map((m) => m.email_address),
    [mailboxes]
  );

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await apiClient.syncMailboxPool();
      showToast(
        `Synced ${result.synced} mailbox${result.synced === 1 ? '' : 'es'}` +
          (result.missing ? `; ${result.missing} no longer at provider` : ''),
        'success'
      );
      await load();
    } catch (error) {
      console.error('Mailbox sync failed:', error);
      showToast(getErrorDetail(error, 'Mailbox sync failed'), 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const markSaving = (email: string, saving: boolean) =>
    setSavingEmails((prev) => {
      const next = new Set(prev);
      if (saving) next.add(email);
      else next.delete(email);
      return next;
    });

  const saveMailbox = async (row: MailboxRow, updates: MailboxAssignUpdates) => {
    markSaving(row.email_address, true);
    try {
      const updated = await apiClient.assignMailbox(row.email_address, updates);
      const campaign = (campaigns ?? []).find((c) => c.id === updated.gtm_project_id);
      const names = {
        project_name: campaign?.name ?? (updated.gtm_project_id === row.gtm_project_id ? row.project_name : null),
        workspace_name:
          campaign?.workspace_name ?? (updated.gtm_project_id === row.gtm_project_id ? row.workspace_name : null),
      };
      setMailboxes((prev) =>
        prev.map((m) => (m.email_address === row.email_address ? { ...m, ...updated, ...names } : m))
      );
      showToast(`Updated ${row.email_address}`, 'success');
    } catch (error) {
      console.error('Mailbox assignment failed:', error);
      showToast(getErrorDetail(error, 'Mailbox update failed'), 'error');
    } finally {
      markSaving(row.email_address, false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" text="Loading mailbox pool..." center />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <section
        id="mailbox-pool"
        className="bg-white border border-neutral-200 rounded-lg shadow-card overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex flex-wrap items-center gap-3">
          <h2 className="text-13 font-semibold text-neutral-900 mr-auto">
            Mailbox pool
            <span className="ml-2 font-normal text-neutral-500">
              {mailboxes.length} mailbox{mailboxes.length === 1 ? '' : 'es'}
            </span>
          </h2>
          <Button variant="secondary" size="sm" onClick={handleSync} disabled={isSyncing}>
            <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} aria-hidden />
            {isSyncing ? 'Syncing…' : 'Sync now'}
          </Button>
        </div>
        {needReconnecting.length > 0 && (
          <p className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            {needReconnecting.length === 1 ? '1 inbox needs' : `${needReconnecting.length} inboxes need`}{' '}
            reconnecting in Instantly, and cannot be assigned until then: {needReconnecting.join(', ')}
          </p>
        )}
        {mailboxes.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Inbox className="w-8 h-8 text-neutral-300 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-neutral-500">
              No mailboxes recorded yet — run a sync to pull the pool.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-xs text-neutral-500">
                <th className="px-5 py-2.5 font-medium">Mailbox</th>
                <th className="px-5 py-2.5 font-medium">Provider</th>
                <th className="px-5 py-2.5 font-medium">Cap/day</th>
                <th className="px-5 py-2.5 font-medium">State</th>
                <th className="px-5 py-2.5 font-medium">Campaign</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {mailboxes.map((row) => (
                <tr
                  key={row.id}
                  className={`hover:bg-neutral-50 transition ${
                    savingEmails.has(row.email_address) ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-5 py-3">
                    <div className="text-neutral-900">{row.email_address}</div>
                    <div className="text-xs text-neutral-500">{row.domain}</div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={PROVIDER_BADGE[row.provider_status]} size="sm">
                      {row.provider_status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-neutral-700">{row.daily_cap}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={stateBadge(row).variant} size="sm">
                        {stateBadge(row).label}
                      </Badge>
                      <Button
                        id={`mailbox-burned-${row.id}`}
                        variant="neutral"
                        size="xs"
                        disabled={savingEmails.has(row.email_address)}
                        aria-label={`${row.warmup_status === 'burned' ? 'Unmark' : 'Mark'} ${row.email_address} as burned`}
                        onClick={() =>
                          row.warmup_status === 'burned'
                            ? setUnburning(row)
                            : void saveMailbox(row, { warmup_status: 'burned' })
                        }
                      >
                        {row.warmup_status === 'burned' ? 'Unmark burned' : 'Mark burned'}
                      </Button>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">{warmupFact(row)}</div>
                  </td>
                  <td className="px-5 py-3">
                    <CustomSelect
                      value={row.gtm_project_id ?? UNASSIGNED}
                      onChange={(v) => void saveMailbox(row, { gtm_project_id: v === UNASSIGNED ? null : v })}
                      options={campaignsLoading ? [] : optionsFor(row)}
                      placeholder="Loading campaigns…"
                      width="240px"
                      searchable
                      disabled={
                        campaignsLoading ||
                        savingEmails.has(row.email_address) ||
                        (!!row.not_ready_reason && !row.gtm_project_id)
                      }
                      aria-label={`Campaign for ${row.email_address}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </section>
      <ConfirmationModal
        isOpen={unburning !== null}
        onClose={() => setUnburning(null)}
        onConfirm={() => {
          if (unburning) void saveMailbox(unburning, { warmup_status: null }).then(() => setUnburning(null));
        }}
        isLoading={unburning !== null && savingEmails.has(unburning.email_address)}
        title="Unmark burned?"
        message={`${unburning?.email_address ?? 'This mailbox'} goes back to its synced state, and can be assigned and send again straight away.`}
        confirmText="Unmark burned"
        variant="warning"
      />
    </div>
  );
};
